import React, { useCallback, useContext, useEffect, useState, useRef, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import VisibilitySensor from 'react-visibility-sensor';
import { withTranslation } from 'react-i18next';
import CreateReusableAsset from '../../components/CreateReusableAssetModal/ReusableAssetModal';
import ComplexPage from './ComplexPage';
import ComplexPageLayoutSettings from './ComplexPageLayoutSettings';
import { useDocumentContext, COMPLEX_DOCUMENT_CONTEXT_KEYS } from '../../context/complexDocumentContext';
import { useArtboardContext, CONTEXT_KEYS ,TEMPLATE_TYPE } from '../../context/complexArtboardContext';
import { getComplexDocument, getStandaloneFlow, getStandaloneBlock, getComplexDocumentStatus, checkLockStatus, lockResource, unlockResource ,getStandaloneFlowStatus ,getStandaloneBlockStatus} from '../../utils/services/complexDocumentsAPI';
import ConfirmationPageDialog from '../../components/Modals/ConfirmationPageDialog';
import { COMPLEX_DOCUMENT_VERSION } from '../../utils/complexDocumentConstants';
import { AddNewFlowButton } from './styles';
import { Icon, message } from 'antd';
import { COMPLEX_PAGE_OPTIONS } from './translationValues';
import { FullPageLoaderContainer } from '../../artboard/styles';
import Loader from '../../components/Loader';
import { BusinessLogic } from '../../context/businessLogicContext';
import { useHistory } from 'react-router-dom';
import { useSessionContext } from '../../context/sessionContext';
import TemplateLockedModal from './TemplateLockedModal';
import ComplexPreviewModal from '../../components/Modals/ComplexPreviewModal';
import { DOCUMENT_DESIGN_SECTION } from '../../artboard/translationValues';
import { patchComplexDocument } from '../../utils/services/complexDocumentsAPI';
import UserActivityListener from './UserActivityListener';
import UpgradePlanDesign from './UpgradePlanDesign';
import AutoSaveBlocker from './AutoSaveBlocker';
import { AppTourContext } from '../../context/appTourContext';
import { useDefaultStyleContext } from '../../context/complexDefaultStyleContext';
import { DEFAULT_FONT_FOR_LOCALISED_LANGUAGES, ENTERPRISE_ENV, checkUserAccessRoles } from '../../utils/helper';
import GridSelectionInterfaceModal from '../../components/Modals/GridSelectionInterfaceModal';
import AddToTocModal from '../../components/Modals/AddToTocModal';
import { PdfInserts } from './ComplexPdfInserts';
import { getInteractiveDesign, updateInteractiveDesign } from '../../utils/services/interactiveDesignApi';
import { getMasterCategory, getAllCategories, createCategory } from '../../utils/services/categoriesAPI';
import { convertStandaloneFlowBlocks, convertTextVariablesInFlows, filterBlocksByBusinessLogic, filterElementsByBusinessLogic, filterFlowByBusinessLogic, filterInsertsByBusinessLogic, repeatBlocks } from '../../utils/interactiveDesignHelper';
import { getIndexedDBData } from './../../utils/indexedDBUtil';
import SignUpPrompt from '../../components/Modals/SignUpPrompt';
import { BusinessLogicModal } from '../../components/BusinessLogicModal';
import { getTemplateDescription } from '../../utils/services/complexDocumentsAPI';
import { checkSessionUserAccess } from '../../utils/helper';

const ComplexArtboard = ({ t, i18n, templateId, visibilityRef, existingDocument = undefined, version = COMPLEX_DOCUMENT_VERSION.DRAFT }) => {
  const customSelector = useCallback((store) => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.FLOWS], []);
  const [flows, store] = useDocumentContext(customSelector, 'ComplexArtboard');

  const customVariableSelector = useCallback(store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.FETCHING_GLOBAL_VARIABLES], []);
  const [isFetchingGlobalVariables] = useDocumentContext(customVariableSelector);

  const editableSelector = useCallback(store => store[CONTEXT_KEYS.ENABLE_EDITABLE_DESIGN], []);
  const [editable] = useArtboardContext(editableSelector, 'ComplexArtboard');

  const isDndFlowSelector = useCallback(store => store[CONTEXT_KEYS.DND_FLOW], []);
  const [isDndFlow] = useArtboardContext(isDndFlowSelector, 'ComplexArtboard');
  
  const lockedUserIdSelector = useCallback(store => store[CONTEXT_KEYS.LOCKED_USER_ID], []);
  const [lockedUserID] = useArtboardContext(lockedUserIdSelector, `ComplexArtboard`);

  const customArtboardSelector = useCallback(store => store[CONTEXT_KEYS.SCALING_SETTING], []);
  const [scaling,,resetArtboard] = useArtboardContext(customArtboardSelector, 'ComplexArtboard');

  const customLoaderSelector = useCallback(store => store[CONTEXT_KEYS.LOADING_DESIGN], []);
  const [isDesignLoading, updateArtboard] = useArtboardContext(customLoaderSelector);

  const getCurrentDesign = useCallback(store => store[CONTEXT_KEYS.CURRENT_DESIGN], []);
  const [CurrentDesign] = useArtboardContext(getCurrentDesign, 'ComplexArtboard');

  const reusableAssetSelector = useCallback((store) => store[CONTEXT_KEYS.REUSABLE_ASSET_MODAL], []);
  const [reusableAssetModal] = useArtboardContext(reusableAssetSelector, 'ComplexArtboard');

  const newFlowDataSelector = useCallback((store) => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.REUSABLE_ASSERT_DATA], []);
  const [reusableAssertData] = useDocumentContext(newFlowDataSelector, 'ComplexArtboard');

  const previewDetailSelector = useCallback(store => store[CONTEXT_KEYS.PREVIEW_MODAL_DETAILS], []);
  const [PreviewModalDetails] = useArtboardContext(previewDetailSelector, `ComplexArtboard`);

  const aiSelector = useCallback(store => store[CONTEXT_KEYS.AI_LOADER], []);
  const [aiLoader] = useArtboardContext(aiSelector, `ComplexArtboard`);

  const interactiveDesignSelector = useCallback(store => store[CONTEXT_KEYS.IS_INTERACTIVE_DESIGN], []);
  const [isInteractiveDesign] = useArtboardContext(interactiveDesignSelector, `ComplexArtboard`);

  const signUpPromptSelector = useCallback(store => store[CONTEXT_KEYS.SHOW_SIGNUP_PROMPT], []);
  const [showSignUpPrompt] = useArtboardContext(signUpPromptSelector, `ComplexArtboard`);

  const openDesignSelector = useCallback(store => store[CONTEXT_KEYS.IS_OPEN_DESIGN], []);
  const [isOpenDesign] = useArtboardContext(openDesignSelector, `ComplexArtboard`);

  const hightlightIdSelector = useCallback((store) => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.GROUP_HIGHLIGHT_ID], []);
  const [highlightId] = useDocumentContext(hightlightIdSelector, 'ComplexArtboard');

  const [templateInserts] = useDocumentContext(
    state => state[COMPLEX_DOCUMENT_CONTEXT_KEYS.TEMPLATE_INSERTS],
  );

  const [modalInfo] = useArtboardContext(store => store[CONTEXT_KEYS.BUSINESS_LOGIC_MODAL]);
  const businessLogicModalVisible = modalInfo ? Object.keys(modalInfo).length > 0 : false;
  const { setBusinessLogics } = useContext(BusinessLogic);
  const [ , defaultStyleStore ] = useDefaultStyleContext(()=> null);
  const currentLanguage = i18n.language;

  const sessionSelector = useCallback(store => store.userData, []);
  const [userDetails] = useSessionContext(sessionSelector);

  const doesSessionExistSelector = useCallback(store => store.userData?.orgStatus === 'active', []);
  const [doesSessionExist] = useSessionContext(doesSessionExistSelector);

  const previewSelector = useCallback(store => store[CONTEXT_KEYS.ENABLE_PAGE_OVERFLOW], []);
  const [isPreview] = useArtboardContext(previewSelector);

  const { isTourActive, setRunAppTour, setStepIndex, setTooltipVisible } = useContext(AppTourContext);

  const history = useHistory();
  
  const { documentId, flowId } = useParams();

  const [showLockedModal, setShowLockedModal] = useState(false);
  const [lockedUserName, setLockedUserName] = useState('');
  const payloadRef = useRef({});
  const lockedUserIDRef = useRef(lockedUserID);

  useEffect(() => {
    lockedUserIDRef.current = lockedUserID;
  }, [lockedUserID]);

  const highlightTimerRef = useRef(null);
  useEffect(() => {
    if (highlightId) {
      if (highlightTimerRef.current) {
        clearTimeout(highlightTimerRef.current);
      }
      highlightTimerRef.current = setTimeout(() => {
        store.set({
          [COMPLEX_DOCUMENT_CONTEXT_KEYS.GROUP_HIGHLIGHT_ID]: null,
        });
        highlightTimerRef.current = null;
      }, 3000);
    } else {
      if (highlightTimerRef.current) {
        clearTimeout(highlightTimerRef.current);
        highlightTimerRef.current = null;
      }
    }
  }, [highlightId]);

  const showServerError = (details, dataError = false) => {
    sessionStorage.setItem('lastUnsuccessfulPayload', JSON.stringify(details));
    updateArtboard({
      [CONTEXT_KEYS.BLOCK_DESIGN]: {
        code: dataError ? '<SER_DATA_ERR>' : '<SER_ERR>',
      },
    });
  };

  const setShowSignUpPrompt = useCallback(() => {
    updateArtboard({
      [CONTEXT_KEYS.SHOW_SIGNUP_PROMPT]: false
    });
  }, []);

  const saveInteractiveDesign = async (interactiveDocument) => {
    const interactiveDesign = interactiveDocument;
    try {
      const response = await updateInteractiveDesign(templateId, interactiveDesign);
      if (response?.statusCode !== 5402) {
        showServerError({templateId, interactiveDesign});
      }
    } catch (e) {
      showServerError({templateId, interactiveDesign, error: e});
    }
  };

  const fetchInteractiveDesign = async () => {
    const fetchingGlobalVariables = store.get()[
      COMPLEX_DOCUMENT_CONTEXT_KEYS.FETCHING_GLOBAL_VARIABLES
    ];
    if (fetchingGlobalVariables) {
      const timeout = setTimeout(() => {
        fetchInteractiveDesign();
        clearTimeout(timeout);
      }, 300);
      return;
    }
    const globalVariables = store.get()[COMPLEX_DOCUMENT_CONTEXT_KEYS.GLOBAL_VARIABLES];
    let response;
    try {
      response = await getInteractiveDesign(templateId);
      if (response?.statusCode === 5401 && response.data) {
        const dataSet = response.data.dataSet;
        let interactiveDocument = response.data.documentDTO;
        // resolve the complex document json into interactive design json if updatedOn is null or undefined
        if (!interactiveDocument.updatedOn) {
          interactiveDocument = filterFlowByBusinessLogic(interactiveDocument, dataSet);
          interactiveDocument = filterBlocksByBusinessLogic(interactiveDocument, dataSet);
          interactiveDocument = filterElementsByBusinessLogic(interactiveDocument, dataSet);
          interactiveDocument = filterInsertsByBusinessLogic(interactiveDocument, dataSet);
          interactiveDocument = repeatBlocks(interactiveDocument, dataSet, true);
          interactiveDocument = await convertTextVariablesInFlows(
            interactiveDocument,
            dataSet,
            globalVariables,
            true
          );
          saveInteractiveDesign(interactiveDocument);
          // interactiveDocument = convertStandaloneFlowBlocks(interactiveDocument)
        }

          interactiveDocument = repeatBlocks(interactiveDocument, dataSet);
          interactiveDocument = await convertTextVariablesInFlows(
            interactiveDocument,
            dataSet,
            globalVariables
          );

        store.setComplexDocumentDesign(interactiveDocument, true, TEMPLATE_TYPE.DOCUMENT);
        setBusinessLogics(interactiveDocument.businessLogics);
        defaultStyleStore.set({
          fontFamily:
            interactiveDocument?.style?.fontFamily ||
            DEFAULT_FONT_FOR_LOCALISED_LANGUAGES(currentLanguage),
          fontSize: interactiveDocument?.style?.fontSize || 16,
        });
        store.set({
          [COMPLEX_DOCUMENT_CONTEXT_KEYS.DATA_SET]: dataSet,
          [COMPLEX_DOCUMENT_CONTEXT_KEYS.INTERACTIVE_DOCUMENT_ID]: templateId,
        });
        
      } else {
        // backend down or some other error response from backend
        showServerError({templateId, response});
      }
    } catch (error) {
      showServerError({templateId, response, error: error}, true);
    } finally {
      updateArtboard({ [CONTEXT_KEYS.LOADING_DESIGN]: false });
    }
  };

  const fetchTemplateDescription = async () => {
    try {
      const response = await getTemplateDescription(documentId);
      if (response?.statusCode === 5502 || response?.statusCode === 5503) {
        if (response.data) {
          store.set({[COMPLEX_DOCUMENT_CONTEXT_KEYS.TEMPLATE_DESCRIPTION]: response.data.description });
        }
      }
    }
    catch(error) {
      console.log(error);      
    }
  };

  useEffect(() => {
    if (templateId === undefined) return;
    let extraArtboardContextChanges = {};
    (async () => {
      let templateType = documentId ? TEMPLATE_TYPE.DOCUMENT : flowId ? TEMPLATE_TYPE.FLOW : TEMPLATE_TYPE.BLOCK;
      updateArtboard({ [CONTEXT_KEYS.CURRENT_DESIGN]: templateType, [CONTEXT_KEYS.LOADING_DESIGN]: true });

      if(templateType === "document") {
        if (isInteractiveDesign) {
          await fetchInteractiveDesign();
        } else if (isOpenDesign) {
          if (doesSessionExist) {
            const design = await getIndexedDBData('masterDocument');
            const actualDesign = await getIndexedDBData('openDocument');
            if (design === undefined) {
              history.push('/');
              return;
            } else {
              // TODO : Get category name from master
              const response = await getMasterCategory();
              const categoryName = response.data.find(e => e.categoryId === actualDesign.categoryId);
              console.log({ response, categoryName });
              // TODO : Get categories from organization filter by category name
              const orgCategoryResponse = await getAllCategories(false, 'template');
              let matchingCategory = undefined;
              if (orgCategoryResponse && orgCategoryResponse.data) {
                matchingCategory = orgCategoryResponse.data.filter(
                  categoryObj => categoryName.categoryName === categoryObj.name,
                );
              }
              console.log({ matchingCategory: matchingCategory.length === 0 });
              if (matchingCategory.length === 0) {
                // TODO : Create a new category with name
                let payload = {
                  name: categoryName.categoryName,
                  type: 'template',
                };
                let categoryResponse = await createCategory(payload);
                matchingCategory = { id: categoryResponse.id };
              } else {
                matchingCategory = matchingCategory[0];
              }
              console.log({ matchingCategory });
              if (actualDesign) {
                extraArtboardContextChanges = Object.assign({}, extraArtboardContextChanges, {
                  [CONTEXT_KEYS.OPEN_MASTER_CLONE]: Object.assign({}, actualDesign, { categoryId: matchingCategory.id })
                });
              }
            }
          } else {
            // Do Nothing
          }
        } else {
          const isMasterDocument = checkSessionUserAccess(userDetails, 'master');
          isMasterDocument && fetchTemplateDescription();
          const existingDocument = await getComplexDocument(templateId, version);
          if (existingDocument.statusCode === 707) {
            existingDocument.data.documentId = templateId;
            store.setComplexDocumentDesign(existingDocument.data, true, templateType);
            setBusinessLogics(existingDocument.data.businessLogics);
            defaultStyleStore.set({
              fontFamily:
                existingDocument.data?.style?.fontFamily ||
                DEFAULT_FONT_FOR_LOCALISED_LANGUAGES(currentLanguage),
              fontSize: existingDocument.data?.style?.fontSize || 16,
            });
            if (version === COMPLEX_DOCUMENT_VERSION.DRAFT) {
              // TODO : Acquire lock for the design to enable or disable readOnly mode
              // TODO : Get status of document
              const documentTimeSpanResponse = await getComplexDocumentStatus(templateId);
              if (documentTimeSpanResponse?.statusCode === 1923) {
                extraArtboardContextChanges = {
                  ...extraArtboardContextChanges,
                  [CONTEXT_KEYS.LIFECYCLE_TIMELINE]: documentTimeSpanResponse.data,
                };
              }
            }
          } else {
            if (version === COMPLEX_DOCUMENT_VERSION.DRAFT) {
              history.push('/notfound');
              return;
            } else {
              console.log('existingDocument :: ', existingDocument);
            }
          }
        }
      } else if(templateType === 'flow') {
        const existingDocument = await getStandaloneFlow(templateId,true,version);        
        if(existingDocument.statusCode === 2605) {
          store.setComplexDocumentDesign(existingDocument.data);
          defaultStyleStore.set({
            fontFamily: existingDocument.data?.flow?.style?.fontFamily || DEFAULT_FONT_FOR_LOCALISED_LANGUAGES(currentLanguage),
            fontSize: existingDocument.data?.flow?.style?.fontSize || 16,
          });
          const flowTimeSpanResponse = await getStandaloneFlowStatus(templateId);          
          if (flowTimeSpanResponse?.statusCode === 1925) {            
            extraArtboardContextChanges = {
              ...extraArtboardContextChanges,
              [CONTEXT_KEYS.LIFECYCLE_TIMELINE]: flowTimeSpanResponse.data
            };
          }
        } else {
          history.push('/notfound');
          return;
        }
      } else if(templateType === 'block') {
        const existingDocument = await getStandaloneBlock(templateId,version);       
        if(existingDocument.statusCode === 2505) {
          store.setComplexDocumentDesign(existingDocument.data);
          defaultStyleStore.set({
            fontFamily: existingDocument.data?.style?.fontFamily || DEFAULT_FONT_FOR_LOCALISED_LANGUAGES(currentLanguage),
            fontSize: existingDocument.data?.style?.fontSize || 16,
          });
          const flowTimeSpanResponse = await getStandaloneBlockStatus(templateId);
          if (flowTimeSpanResponse?.statusCode === 1927) {
            extraArtboardContextChanges = {
              ...extraArtboardContextChanges,
              [CONTEXT_KEYS.LIFECYCLE_TIMELINE]: flowTimeSpanResponse.data
            };
          }
        } else {
          history.push('/notfound');
          return;
        }
      }

      if((templateType === 'flow' || templateType === 'block') && !checkUserAccessRoles(userDetails, ['REUSABLECONTENTMANAGER'])) {
        updateArtboard({
          ...extraArtboardContextChanges,
          [CONTEXT_KEYS.TEMPLATE_LOCKED]: true,
          [CONTEXT_KEYS.ENABLE_EDITABLE_DESIGN]: false,
          [CONTEXT_KEYS.LOADING_DESIGN]: false,
        });
        return;
      }

      if (version === COMPLEX_DOCUMENT_VERSION.DRAFT && !isOpenDesign) {
        let payload = {};
        payload.resourceId = templateId;
        payload.resourceType = isInteractiveDesign ? 'interactive_design' : templateType;
        payloadRef.current = payload;
  
        const response = await checkLockStatus(payload);
        if(response) {
          if(response.statusCode === 740) {
            // Lock the template
            const lockResponse = await lockResource(payload);
            if(lockResponse.statusCode === 719) {
              extraArtboardContextChanges = {
                ...extraArtboardContextChanges,
                [CONTEXT_KEYS.LOCKED_USER_ID]: lockResponse.data.userId
              };
              // Enable guidance tooltips in the design area if the template is not locked.
              templateType === "document" && setTooltipVisible(true);
            }
          } else if(response.statusCode === 741) {
            extraArtboardContextChanges = {
              ...extraArtboardContextChanges,
              [CONTEXT_KEYS.LOCKED_USER_ID]: response.data.userId
            };
            /* Check if the same user is trying to access the template,
            if not show template is locked modal and render the template in read-only mode */
            if(userDetails.id !== response.data.userId) {
              setLockedUserName(response.data.userName);
              setShowLockedModal(true);
              extraArtboardContextChanges = {
                ...extraArtboardContextChanges,
                [CONTEXT_KEYS.TEMPLATE_LOCKED]: true,              
                [CONTEXT_KEYS.ENABLE_EDITABLE_DESIGN]: false
              };
            } else {
              templateType === "document" && setTooltipVisible(true);
            }
          }
        }
        // Continue the tour if the user is logged in for the first time
        if(isTourActive) {
          setStepIndex(prevIndex => prevIndex + 1);
          setRunAppTour(true);
        }
      }
      if(!navigator.onLine || isOpenDesign) {
        const currentDocument = await getIndexedDBData('openDocument');
        store.setComplexDocumentDesign(currentDocument, false);
      }
      updateArtboard({ [CONTEXT_KEYS.LOADING_DESIGN]: false, ...extraArtboardContextChanges });
    })();
  }, [templateId, doesSessionExist, version]);

  useEffect(() => {
    if (existingDocument === undefined) return;
    store.setComplexDocumentDesign(existingDocument, false, CurrentDesign);
    const fontFamily = existingDocument?.flow
      ? existingDocument?.flow?.style?.fontFamily ||
        DEFAULT_FONT_FOR_LOCALISED_LANGUAGES(currentLanguage)
      : (CurrentDesign === "flow" && existingDocument?.flows && existingDocument?.flows[0])
      ? existingDocument?.flows[0]?.style?.fontFamily ||
        DEFAULT_FONT_FOR_LOCALISED_LANGUAGES(currentLanguage)
      : DEFAULT_FONT_FOR_LOCALISED_LANGUAGES(currentLanguage);
    const fontSize = existingDocument?.flow
      ? existingDocument?.flow?.style?.fontSize || 16
      : (CurrentDesign === "flow" && existingDocument?.flows && existingDocument?.flows[0])
      ? existingDocument?.flows[0]?.style?.fontSize || 16
      : 16;
    defaultStyleStore.set({
      fontFamily: existingDocument?.style?.fontFamily || fontFamily,
      fontSize: existingDocument?.style?.fontSize || fontSize,
    });
    setBusinessLogics(existingDocument.businessLogics);
    updateArtboard({ [CONTEXT_KEYS.LOADING_DESIGN]: false });
  }, [existingDocument]);


  const addNewFlowToEnd = useCallback(() => {
    store.addNewFlow();
  }, []);

  useEffect(() => {
    // remove datasetCache on mount so it will be removed on refresh/go back in browser history.
    // so latest variables for the document is re-generated.
    // don't remove datasetCache on preview mode.
    !isPreview && sessionStorage.removeItem('datasetCache');
    const updateVariables = () => {
      store.fetchAllVariables();
      store.fetchAllVariablesRelations();
    }
    window.addEventListener("variablesUpdated", updateVariables);

    return () => {
      console.log("Unmounting artboard!!");
      store.clearDesign();
      resetArtboard();
      window.removeEventListener("variablesUpdated", updateVariables);
    }
  }, []);

  useEffect(() => {
    return async () => {
      // Unlock the template on component unmount
      if (userDetails?.id === lockedUserIDRef.current) {
        await unlockResource(payloadRef.current);
      }
    }
  }, [documentId]);

  const disableModalVisibility = () => {
    store.set({ [COMPLEX_DOCUMENT_CONTEXT_KEYS.REUSABLE_ASSERT_DATA]: {} });
    updateArtboard({ [CONTEXT_KEYS.REUSABLE_ASSET_MODAL]: false })  
  };

  const updateFlow = async(flowId,isAttach,flowUUID) => {
    disableModalVisibility();
    if(isAttach){
      const payload =  store.createDocumentPayload(flowId,flowUUID);
      await patchComplexDocument(payload,flowUUID)
    }
  };

  const disablePreviewModal = () => {
    updateArtboard({ [CONTEXT_KEYS.PREVIEW_MODAL_DETAILS]: { visible: false, } })
  }

  const onCancelBusinessLogicModal = () => {
    updateArtboard({ [CONTEXT_KEYS.BUSINESS_LOGIC_MODAL]: undefined });
  };

  const totalPages = useMemo(() => {
    const totalInsertsPages = templateInserts.reduce(
      (acc, { pages }) => Number(acc) + Number(pages),
      0,
    );
    return flows.length + totalInsertsPages;
  }, [flows, templateInserts]);

   return (
    <>
      {(isFetchingGlobalVariables || isDesignLoading) ? (
        <FullPageLoaderContainer>
          <Loader fullpage="true" />
        </FullPageLoaderContainer>
      ) : (
        <>
          {flows.map((flow, flowNumber) => (
            <>
              <VisibilitySensor
                key={flow.uuid}
                containment={visibilityRef?.current}
                partialVisibility={true}
                offset={{ top: 500 }} // TODO : Check if this has to be configured by page size or orientation
                minTopValue={400}
              >
                {({ isVisible }) => {
                  return (
                    <ComplexPage
                      key={`${flow}-${flowNumber}`}
                      uuid={flow}
                      flowNumber={flowNumber}
                      totalPages={flows.length}
                      totalPagesWithInserts={totalPages}
                      // className={}
                      scaling={scaling}
                      isVisible={isVisible}
                      visibilityRef={visibilityRef?.current}
                      isReadOnlyMode={editable}
                    />
                  );
                }}
              </VisibilitySensor>
            </>
          ))}
             {editable && !isInteractiveDesign && CurrentDesign === 'document' &&  (
               <AddNewFlowButton onClick={addNewFlowToEnd} document={true} isInsertsAvailable={templateInserts?.length > 0}>
                 <Icon type="plus" />
                 {t(
                   COMPLEX_PAGE_OPTIONS.ADD_NEW_PAGE_BUTTON.key,
                   COMPLEX_PAGE_OPTIONS.ADD_NEW_PAGE_BUTTON.value,
                 )}
               </AddNewFlowButton>
             )}
             {editable && CurrentDesign !== 'document' && (<AddNewFlowButton />)}
             {ENTERPRISE_ENV && CurrentDesign === 'document' && !isDndFlow ? <PdfInserts totalPages={totalPages} lastPage={flows.length}/> : null}
          <ComplexPageLayoutSettings />
          <ConfirmationPageDialog />
          <GridSelectionInterfaceModal/>
          <AddToTocModal/>
          {businessLogicModalVisible && <BusinessLogicModal modalInfo= {modalInfo} onCancel= {onCancelBusinessLogicModal}/> }
          {reusableAssetModal &&
            <CreateReusableAsset
              onSucess={updateFlow}
              onCancel={disableModalVisibility}                
              type={reusableAssertData.type}
            />
          }
          <UserActivityListener checkLockStatus={() => checkLockStatus(payloadRef.current)} />
          <UpgradePlanDesign />
          <AutoSaveBlocker />
        </>
      )}    
      { showLockedModal && <TemplateLockedModal
          visible={showLockedModal}
          onClose={() => setShowLockedModal(false)}
          lockedByUser={lockedUserName}
      /> }
       {PreviewModalDetails?.visible &&
         <ComplexPreviewModal
           visible={PreviewModalDetails.visible}
           onCancel={disablePreviewModal}
           type={'flow'}          
           templateId={PreviewModalDetails.flowId}
           templateData={PreviewModalDetails.flowData || undefined}
           reusableFlow={PreviewModalDetails.isStandalone}
         />
       }
       {aiLoader && (
        <FullPageLoaderContainer>
          <Loader
            text={t(
              DOCUMENT_DESIGN_SECTION.AI_AUTO_FILL_CONTENT_MODAL_INFO.key,
              DOCUMENT_DESIGN_SECTION.AI_AUTO_FILL_CONTENT_MODAL_INFO.value,
            )}
          />
        </FullPageLoaderContainer>
      )}
      {/* Instantiate the SignUpPrompt modal for demonstration */}
      <SignUpPrompt
        visible={isOpenDesign && showSignUpPrompt}
        onCancel={() => setShowSignUpPrompt(false)}
        onSignUp={() => {
          setShowSignUpPrompt(false);
          // TODO: Add sign up logic
        }}
      />
    </>
  );
}

export default React.memo(withTranslation(['COMPLEX_PAGE_OPTIONS'])(ComplexArtboard));